// Deep-link URL vocabulary — the single source of truth for turning app state
// into human-readable path segments and back. Like sections.ts and
// variables.ts, this is a dependency-free ES module reached from both halves of
// the codebase: the browser imports it as a static asset, and the Node server
// reaches it by relative path (src/server/tiles.js already imports variables.ts
// the same way). Keeping the slug ↔ key maps here means the client that writes a
// URL and the server that resolves it can never drift.
//
// Path grammar: /{country}/{city?}/{date?}/{mode?}/
//   country  slugified country display name        (united-kingdom)
//   city     slugified city ASCII name             (london)
//   date     {early|mid|late}-{month}              (mid-may)   ← a 36-section key
//   mode     slugified map-field label             (easy-outdoors)
// A city present ⇒ location view; absent ⇒ map view focused on the country.

import { SECTION_KEYS, MONTH_NAMES, PART_LABELS, parseSection } from './sections.ts';
import { VAR, VARIANT_GROUPS } from './variables.ts';
import type { VarKey } from './variables.ts';
import { BUCKETS } from './buckets.ts';

// A map-view camera, decoded from an "@lat,lng,zoom" token.
export interface RouteCoord {
  lat: number;
  lng: number;
  zoom: number;
}

// A path decomposed into the grammar's parts; null wherever the path names none.
export interface ParsedRoute {
  coord: RouteCoord | null;
  countrySlug: string | null;
  citySlug: string | null;
  dateSlug: string | null;
  modeSlug: string | null;
}

// Lowercase, strip diacritics, collapse every non-alphanumeric run to a single
// dash. Diacritic folding matters: a city carried as "São Paulo" and its ASCII
// twin "Sao Paulo" must slug to the same "sao-paulo", so the client (which only
// holds the display name) and the server (which holds nameAscii) agree.
export function slugify(s: unknown): string {
  return String(s == null ? '' : s)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function countrySlug(name: string | null | undefined): string {
  return slugify(name);
}
export function citySlug(name: string | null | undefined): string {
  return slugify(name);
}

// ── date (calendar section) ─────────────────────────────────────────────────
// Section key "05-2" ⇄ "mid-may". PART_LABELS = ['Early','Mid','Late'].
export function dateSlugForKey(key: string): string {
  const { month, part } = parseSection(key);
  return `${PART_LABELS[part - 1].toLowerCase()}-${MONTH_NAMES[month - 1].toLowerCase()}`;
}
const DATE_BY_SLUG = new Map(SECTION_KEYS.map((k) => [dateSlugForKey(k), k] as const));
export function keyForDateSlug(slug: unknown): string | null {
  return DATE_BY_SLUG.get(String(slug == null ? '' : slug).toLowerCase()) || null;
}
export function isDateSlug(slug: unknown): boolean {
  return DATE_BY_SLUG.has(String(slug == null ? '' : slug).toLowerCase());
}

// ── mode (map field) ────────────────────────────────────────────────────────
// Only the user-selectable fields are addressable: the two variant-group chips
// and their toggle members, plus the chip-bearing activity scores. Restricting
// to these guarantees varForModeSlug() always returns a key applyVariable
// accepts.
const MODE_KEYS = (() => {
  const set = new Set<VarKey>();
  for (const group of Object.values(VARIANT_GROUPS)) {
    for (const [key] of group.members) set.add(key);
  }
  for (const key of Object.keys(VAR) as VarKey[]) {
    if (VAR[key].chip) set.add(key);
  }
  return [...set];
})();
// The key is whatever the caller holds — app state or a URL — so it is not known
// to name a variable until the lookup says so.
export function modeSlugForVar(varKey: string): string | null {
  return VAR[varKey as VarKey] ? slugify(VAR[varKey as VarKey].label) : null;
}
const VAR_BY_SLUG = new Map(MODE_KEYS.map((k) => [modeSlugForVar(k), k] as const));
export function varForModeSlug(slug: unknown): VarKey | null {
  return VAR_BY_SLUG.get(String(slug == null ? '' : slug).toLowerCase()) || null;
}
export function isModeSlug(slug: unknown): boolean {
  return VAR_BY_SLUG.has(String(slug == null ? '' : slug).toLowerCase());
}

// A map-view camera token, "@51.5074,-0.1278,4.3" = lat,lng,zoom. It remembers
// the exact view a reader shared or left, so Back/Forward restores that camera
// rather than snapping to a country. Only the map view emits one; city links use
// country/city slugs instead.
const COORD_RE = /^@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?),(\d+(?:\.\d+)?)$/;

// ── path ⇄ parts ────────────────────────────────────────────────────────────
// Classify positionally with a vocabulary fallback. A camera token is pulled out
// first, wherever it sits (map view). The leading remaining segment is the
// country UNLESS it is itself a date or mode token. Among the rest, a date token
// is the date and a mode token is the mode; the first token that is neither is
// the city (city slugs are open-vocabulary, date/mode are closed).
export function parseRoute(pathname: unknown): ParsedRoute {
  const raw = String(pathname == null ? '' : pathname)
    .split('/')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
  const out: ParsedRoute = { coord: null, countrySlug: null, citySlug: null, dateSlug: null, modeSlug: null };
  const segs: string[] = [];
  for (const s of raw) {
    const m = COORD_RE.exec(s);
    if (m && !out.coord) out.coord = { lat: Number(m[1]), lng: Number(m[2]), zoom: Number(m[3]) };
    else segs.push(s);
  }
  let i = 0;
  if (segs.length && !isDateSlug(segs[0]) && !isModeSlug(segs[0])) {
    out.countrySlug = segs[0];
    i = 1;
  }
  for (; i < segs.length; i++) {
    const s = segs[i];
    if (out.dateSlug == null && isDateSlug(s)) out.dateSlug = s;
    else if (out.modeSlug == null && isModeSlug(s)) out.modeSlug = s;
    else if (out.citySlug == null) out.citySlug = s;
  }
  return out;
}

// Build a path from parts, dropping empties, always with a trailing slash (and
// "/" for the empty landing route). A map camera is emitted as the leading
// "@lat,lng,zoom" token; otherwise the country/city slugs lead. The inverse of
// parseRoute for any parts it itself would produce.
export function routePath({ coord, countrySlug: co, citySlug: ci, dateSlug: da, modeSlug: mo }: Partial<ParsedRoute> = {}): string {
  const segs: string[] = [];
  if (coord && Number.isFinite(coord.lat) && Number.isFinite(coord.lng) && Number.isFinite(coord.zoom)) {
    segs.push(`@${coord.lat},${coord.lng},${coord.zoom}`);
  } else {
    if (co) segs.push(co);
    if (ci) segs.push(ci);
  }
  if (da) segs.push(da);
  if (mo) segs.push(mo);
  return segs.length ? `/${segs.join('/')}/` : '/';
}

// ── the two views that are about places rather than inside one ─────────────
// /day/{place}/{date}/{bucket}/            a day you loved, as a rating
// /compare/{a}/{b}[/{c}][/{date}][/{mode}][/full]/   two or three places side by side
//
// {place} is a country-free city slug (assignPlaceSlugs in city-slugs.ts): the
// server alone knows which namesake owns the bare spelling, so the client never
// derives one — it carries the slug every payload hands it. {bucket} is the time
// of day as the sentence says it (midday), not its internal key (lunch).
//
// Both are addresses of a VIEW, not canonical pages: they are not in the
// sitemap and carry their own <title> only so a shared link unfurls as what it
// is.


export function bucketSlugForKey(key: string): string | null {
  const b = BUCKETS.find((x) => x.key === key);
  return b ? slugify(b.label) : null;
}
export function bucketKeyForSlug(slug: unknown): string | null {
  const s = String(slug == null ? '' : slug).toLowerCase();
  const b = BUCKETS.find((x) => slugify(x.label) === s || x.key === s);
  return b ? b.key : null;
}

export interface DayRoute {
  place: string;
  dateSlug: string | null;
  bucket: string | null; // internal key
}

export interface CompareRoute {
  places: string[];
  dateSlug: string | null;
  modeSlug: string | null;
  full: boolean;
}

const segsOf = (pathname: unknown): string[] =>
  String(pathname == null ? '' : pathname).split('/').map((s) => s.trim().toLowerCase()).filter(Boolean);

export function parseDayRoute(pathname: unknown): DayRoute | null {
  const segs = segsOf(pathname);
  if (segs[0] !== 'day' || !segs[1]) return null;
  let dateSlug: string | null = null;
  let bucket: string | null = null;
  for (const s of segs.slice(2)) {
    if (dateSlug == null && isDateSlug(s)) dateSlug = s;
    else if (bucket == null && bucketKeyForSlug(s)) bucket = bucketKeyForSlug(s);
  }
  return { place: segs[1], dateSlug, bucket };
}

export function dayPath({ place, dateKey, bucket }: { place: string; dateKey?: string | null; bucket?: string | null }): string {
  const segs = ['day', place];
  if (dateKey) segs.push(dateSlugForKey(dateKey));
  const b = bucket ? bucketSlugForKey(bucket) : null;
  if (b) segs.push(b);
  return `/${segs.join('/')}/`;
}

export const MAX_COMPARE = 3;

export function parseCompareRoute(pathname: unknown): CompareRoute | null {
  const segs = segsOf(pathname);
  if (segs[0] !== 'compare') return null;
  const out: CompareRoute = { places: [], dateSlug: null, modeSlug: null, full: false };
  for (const s of segs.slice(1)) {
    if (s === 'full') out.full = true;
    else if (out.dateSlug == null && isDateSlug(s)) out.dateSlug = s;
    else if (out.modeSlug == null && isModeSlug(s)) out.modeSlug = s;
    else if (out.places.length < MAX_COMPARE && !out.places.includes(s)) out.places.push(s);
  }
  return out.places.length ? out : null;
}

export function comparePath({ places, dateSlug = null, modeSlug = null, full = false }: Partial<CompareRoute> & { places: string[] }): string {
  const segs = ['compare', ...places.slice(0, MAX_COMPARE)];
  if (dateSlug) segs.push(dateSlug);
  if (modeSlug) segs.push(modeSlug);
  if (full) segs.push('full');
  return `/${segs.join('/')}/`;
}
